import { createContext, useContext, useMemo } from 'react';
import { ApplicationTargetEnum } from '~types/app-selection';
import { qcTheme } from './qc';
import { caTheme } from './ca';
import { PREVIEW_CSS_VARS, PreviewRegion, PreviewTheme } from './tokens';

const PreviewThemeContext = createContext<PreviewTheme | null>(null);

const themeByRegion: Record<PreviewRegion, PreviewTheme> = {
    [ApplicationTargetEnum.QC]: qcTheme,
    [ApplicationTargetEnum.CA]: caTheme,
};

type PreviewThemeProviderProps = {
    region: PreviewRegion;
    children: React.ReactNode;
};

/**
 * Resolves the preview theme for the given region and exposes it to its
 * subtree both as a React context (for TS consumers via
 * `usePreviewTheme()`) and as scoped CSS custom properties on a wrapper
 * `<div>` (for SCSS modules using `var(--preview-primary)` etc.).
 *
 * The CSS vars are intentionally namespaced with `--preview-…` so they
 * don't clash with the global CMS tokens declared in
 * `styles/_design-tokens.scss`.
 */
export const PreviewThemeProvider = ({
    region,
    children,
}: PreviewThemeProviderProps) => {
    const theme = themeByRegion[region];

    const cssVars = useMemo(
        () => ({
            [PREVIEW_CSS_VARS.primary]: theme.colors.primary,
            [PREVIEW_CSS_VARS.primaryContrast]: theme.colors.primaryContrast,
        }),
        [theme]
    );

    return (
        <PreviewThemeContext.Provider value={theme}>
            <div
                className="preview-theme-root"
                style={cssVars as React.CSSProperties}
            >
                {children}
            </div>
        </PreviewThemeContext.Provider>
    );
};

/**
 * Read the active preview theme. Throws if used outside a
 * `<PreviewThemeProvider />` so misuse fails loudly during development
 * rather than rendering with stale or default values.
 */
export const usePreviewTheme = (): PreviewTheme => {
    const theme = useContext(PreviewThemeContext);
    if (!theme) {
        throw new Error(
            'usePreviewTheme must be used within a PreviewThemeProvider'
        );
    }
    return theme;
};
